import { useCallback, useMemo, useRef, useState } from 'react'
import { useFocusEffect } from 'expo-router'
import { useQueryClient } from '@tanstack/react-query'
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import type {
  B2bAccountUserData,
  B2bBrandingData,
  B2bMemberRow,
  B2bAccountCreatePayload,
  B2bJoinOrganizationResponse,
} from '../types/b2bAccount.types'
import type { OrgInvoiceProfilePatch } from '../helpers/orgInvoiceProfile.helper'
import { getB2bSelfLeaveUiState } from '../helpers/b2bAccount.helper'
import { worldCupQueryKeys } from '../../worldcup/constants/worldcupQueryKeys'
import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants'

interface ApiEnvelope<T> {
  success?: boolean
  data?: T
  error?: string
}

function normalizeConsumerBilling(user: B2bAccountUserData): B2bAccountUserData {
  if (user.consumerBilling) {
    return user
  }

  return {
    ...user,
    consumerBilling: {
      planTier: 'FREE',
      proBillingInterval: null,
      stripePortalAvailable: false,
      stripeBillingEnabled: false,
      stripeCurrentPeriodEnd: null,
      stripeSubscriptionStatus: null,
      cancelAtPeriodEnd: false,
    },
  }
}

export type { B2bJoinOrganizationResponse } from '../types/b2bAccount.types'

export function useAccountAdmin() {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const [user, setUser] = useState<B2bAccountUserData | null>(null)
  const [branding, setBranding] = useState<B2bBrandingData | null>(null)
  const [members, setMembers] = useState<B2bMemberRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** Monotonic id so overlapping `load()` calls (e.g. focus + billing sync) cannot apply stale responses. */
  const loadSerialRef = useRef(0)
  /** Nested spinner requests (e.g. rapid refocus) keep loading until the last spinner load finishes. */
  const spinnerDepthRef = useRef(0)
  /**
   * True after we've successfully loaded B2B account + membership at least once this session.
   * Used by useFocusEffect without listing `user` in deps — otherwise every `setUser` re-runs focus
   * and hammers GET /accounts/user + branding + members in a loop.
   */
  const hasAccountAdminPayloadRef = useRef(false)

  const load = useCallback(
    async (showSpinner: boolean) => {
      const loadId = ++loadSerialRef.current
      if (showSpinner) {
        spinnerDepthRef.current += 1
        if (spinnerDepthRef.current === 1) {
          setLoading(true)
        }
      }
      setError(null)
      try {
        const res = await apiClient<ApiEnvelope<B2bAccountUserData | null>>(API_ENDPOINTS.accounts.user)
        if (loadId !== loadSerialRef.current) {
          return
        }
        const payload = res.data
        const data = payload?.data ?? null
        if (!data) {
          hasAccountAdminPayloadRef.current = false
          setUser(null)
          setBranding(null)
          setMembers([])
          return
        }
        const normalizedUser = normalizeConsumerBilling(data)
        setUser(normalizedUser)
        if (!normalizedUser.membership || !normalizedUser.account) {
          hasAccountAdminPayloadRef.current = false
          setBranding(null)
          setMembers([])
          return
        }
        hasAccountAdminPayloadRef.current = true
        if (normalizedUser.membership.role === 'ADMIN') {
          // GET branding uses requireActiveB2bMember; inactive orgs get 403 and must not fail the whole load
          // (admins use PATCH /accounts/:id for reactivate/deactivate; members list uses requireB2bAdmin only).
          if (!normalizedUser.account.isActive) {
            setBranding(null)
            const memRes = await apiClient<ApiEnvelope<B2bMemberRow[]>>(
              API_ENDPOINTS.accounts.members(normalizedUser.account.id),
            )
            if (loadId !== loadSerialRef.current) {
              return
            }
            setMembers(memRes.data?.data ?? [])
          } else {
            const [brRes, memRes] = await Promise.all([
              apiClient<ApiEnvelope<B2bBrandingData>>(
                API_ENDPOINTS.accounts.branding(normalizedUser.account.id),
              ),
              apiClient<ApiEnvelope<B2bMemberRow[]>>(
                API_ENDPOINTS.accounts.members(normalizedUser.account.id),
              ),
            ])
            if (loadId !== loadSerialRef.current) {
              return
            }
            const rawBranding = brRes.data?.data
            if (rawBranding) {
              const logoCdnUrlsFromApi = Array.isArray(rawBranding.logoCdnUrls)
                ? rawBranding.logoCdnUrls.filter((u): u is string => typeof u === 'string' && u.length > 0)
                : []
              const logoCdnUrls =
                logoCdnUrlsFromApi.length > 0
                  ? logoCdnUrlsFromApi
                  : rawBranding.logoCdnUrl
                    ? [rawBranding.logoCdnUrl]
                    : []
              const logoUrlsFromApi = Array.isArray(rawBranding.logoUrls)
                ? rawBranding.logoUrls.filter((u): u is string => typeof u === 'string' && u.length > 0)
                : []
              const logoUrls =
                logoUrlsFromApi.length > 0
                  ? logoUrlsFromApi
                  : rawBranding.logoUrl
                    ? [rawBranding.logoUrl]
                    : []
              setBranding({ ...rawBranding, logoCdnUrls, logoUrls })
            } else {
              setBranding(null)
            }
            setMembers(memRes.data?.data ?? [])
          }
        } else {
          setBranding(null)
          setMembers([])
        }
      } catch (e) {
        if (loadId !== loadSerialRef.current) {
          return
        }
        setError(extractErrorMessage(e, 'Could not load organization'))
        hasAccountAdminPayloadRef.current = false
        setUser(null)
        setBranding(null)
        setMembers([])
      } finally {
        if (showSpinner) {
          spinnerDepthRef.current -= 1
          if (spinnerDepthRef.current <= 0) {
            spinnerDepthRef.current = 0
            setLoading(false)
          }
        }
      }
    },
    [apiClient],
  )

  const invalidateWorldCupSquadsList = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: worldCupQueryKeys.groups(WORLD_CUP_COMPETITION_ID),
    })
  }, [queryClient])

  const refresh = useCallback(async () => {
    const linkedGroupId = user?.account?.defaultWorldCupGroup?.id ?? null
    await load(false)
    if (!linkedGroupId) return
    invalidateWorldCupSquadsList()
    await queryClient.invalidateQueries({
      queryKey: worldCupQueryKeys.groupLeaderboard(WORLD_CUP_COMPETITION_ID, linkedGroupId, { limit: 100 }),
    })
  }, [load, user?.account?.defaultWorldCupGroup?.id, queryClient, invalidateWorldCupSquadsList])

  useFocusEffect(
    useCallback(() => {
      let active = true
      void (async () => {
        if (!active) return
        // Full-screen spinner only on first successful org payload; refetch on focus stays silent.
        // Do not close over `user` in deps — each load() updates `user` and would re-fire this effect forever.
        await load(!hasAccountAdminPayloadRef.current)
      })()
      return () => {
        active = false
      }
    }, [load]),
  )


  const createOrganization = useCallback(
    async (payload: B2bAccountCreatePayload) => {
      const res = await apiClient<ApiEnvelope<{ accountId: string; joinCode: string }>>(
        API_ENDPOINTS.accounts.root,
        { method: 'POST', data: payload },
      )
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Create failed')
      }
      invalidateWorldCupSquadsList()
      await load(false)
    },
    [apiClient, invalidateWorldCupSquadsList, load],
  )

  const joinOrganization = useCallback(
    async (joinCode: string): Promise<B2bJoinOrganizationResponse | undefined> => {
      const res = await apiClient<ApiEnvelope<B2bJoinOrganizationResponse>>(API_ENDPOINTS.accounts.join, {
        method: 'POST',
        data: { joinCode },
      })
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Join failed')
      }
      invalidateWorldCupSquadsList()
      await load(false)
      return res.data.data
    },
    [apiClient, invalidateWorldCupSquadsList, load],
  )

  const leaveOrganization = useCallback(async () => {
    const res = await apiClient<ApiEnvelope<{ left: boolean; accountId: string }>>(
      API_ENDPOINTS.accounts.leave,
      { method: 'POST' },
    )
    if (!res.data?.success) {
      throw new Error(res.data?.error || 'Leave failed')
    }
    invalidateWorldCupSquadsList()
    await load(false)
  }, [apiClient, invalidateWorldCupSquadsList, load])

  const saveSsoSettings = useCallback(
    async (allowedEmailDomain: string | null) => {
      if (!user?.account?.id) return
      const res = await apiClient(API_ENDPOINTS.accounts.ssoSettings(user.account.id), {
        method: 'PATCH',
        data: { allowedEmailDomain },
      })
      const body = res.data as ApiEnvelope<unknown>
      if (!body?.success) {
        throw new Error((body as { error?: string })?.error || 'Save failed')
      }
      await load(false)
    },
    [apiClient, user?.account?.id, load],
  )

  const patchBrandingColors = useCallback(
    async (input: {
      primaryColor?: string
      secondaryColor?: string
      displayName?: string | null
      useBannerBackground?: boolean
    }) => {
      if (!user?.account?.id) return
      const res = await apiClient(API_ENDPOINTS.accounts.branding(user.account.id), {
        method: 'PATCH',
        data: input,
      })
      const body = res.data as ApiEnvelope<unknown>
      if (!body?.success) {
        throw new Error(body?.error || 'Update failed')
      }
      invalidateWorldCupSquadsList()
      await load(false)
    },
    [apiClient, invalidateWorldCupSquadsList, user?.account?.id, load],
  )

  const patchAccountSettings = useCallback(
    async (input: {
      name?: string
      isActive?: boolean
      unlimitedMembers?: boolean
      maxSeats?: number
      worldCupDefaultSquadInviteCode?: string | null
      /** Renames the competition squad currently linked as `defaultWorldCupGroup` (must be WC squad admin). */
      linkedWorldCupSquadName?: string
    } & OrgInvoiceProfilePatch) => {
      if (!user?.account?.id) return
      const res = await apiClient(API_ENDPOINTS.accounts.account(user.account.id), {
        method: 'PATCH',
        data: input,
      })
      const body = res.data as ApiEnvelope<unknown>
      if (!body?.success) {
        throw new Error(body?.error || 'Update failed')
      }
      if (
        input.worldCupDefaultSquadInviteCode !== undefined ||
        input.linkedWorldCupSquadName !== undefined
      ) {
        invalidateWorldCupSquadsList()
      }
      await load(false)
    },
    [apiClient, invalidateWorldCupSquadsList, user?.account?.id, load],
  )

  const removeMember = useCallback(
    async (memberUserId: string) => {
      if (!user?.account?.id) return
      const res = await apiClient(API_ENDPOINTS.accounts.member(user.account.id, memberUserId), {
        method: 'DELETE',
      })
      const body = res.data as ApiEnvelope<unknown>
      if (!body?.success) {
        throw new Error(body?.error || 'Remove failed')
      }
      await load(false)
    },
    [apiClient, user?.account?.id, load],
  )

  const removeMembersBulk = useCallback(
    async (userIds: string[]) => {
      if (!user?.account?.id || userIds.length === 0) return
      const res = await apiClient<
        ApiEnvelope<{ removed: number; skipped: { userId: string; reason: string }[] }>
      >(API_ENDPOINTS.accounts.membersBulkRemove(user.account.id), {
        method: 'POST',
        data: { userIds },
      })
      const body = res.data
      if (!body?.success) {
        throw new Error(body?.error || 'Bulk remove failed')
      }
      await load(false)
      return body.data ?? { removed: 0, skipped: [] as { userId: string; reason: string }[] }
    },
    [apiClient, user?.account?.id, load],
  )

  const patchMemberRole = useCallback(
    async (memberUserId: string, role: 'ADMIN' | 'MEMBER') => {
      if (!user?.account?.id) return
      const res = await apiClient(API_ENDPOINTS.accounts.member(user.account.id, memberUserId), {
        method: 'PATCH',
        data: { role },
      })
      const body = res.data as ApiEnvelope<unknown>
      if (!body?.success) {
        throw new Error(body?.error || 'Update role failed')
      }
      await load(false)
    },
    [apiClient, user?.account?.id, load],
  )

  const deleteOrganizationPermanently = useCallback(
    async (confirmDeletionPhrase: string) => {
      if (!user?.account?.id) return
      const res = await apiClient(API_ENDPOINTS.accounts.account(user.account.id), {
        method: 'DELETE',
        data: { confirmDeletionPhrase: confirmDeletionPhrase.trim() },
      })
      const body = res.data as ApiEnvelope<unknown>
      if (!body?.success) {
        throw new Error(body?.error || 'Delete failed')
      }
      await load(false)
    },
    [apiClient, user?.account?.id, load],
  )

  const isAdmin = user?.membership?.role === 'ADMIN'
  const accountId = user?.account?.id ?? null
  const canLeaveOrganization = useMemo(
    () => getB2bSelfLeaveUiState(Boolean(isAdmin), members).canLeave,
    [isAdmin, members],
  )

  return {
    user,
    branding,
    members,
    loading,
    error,
    refresh,
    isAdmin,
    accountId,
    canLeaveOrganization,
    createOrganization,
    joinOrganization,
    leaveOrganization,
    deleteOrganizationPermanently,
    patchBrandingColors,
    patchAccountSettings,
    saveSsoSettings,
    removeMember,
    removeMembersBulk,
    patchMemberRole,
  }
}
